<template>
  <div class="card card-body">
    <!-- DOWNLOAD SAMPLE -->
    <!-- <button class="btn btn-outline-secondary mb-2" @click="downloadSample">
      <ASLDeductions/>
    </button> -->

    <button class="btn btn-outline-secondary mb-2">
      <ASLDeductions />
    </button>

    <!-- FILE UPLOAD -->
    <input type="file" accept=".xlsx,.xls" @change="handleFile" class="form-control mb-3" />

    <!-- SEARCH -->
    <input v-if="rows.length" class="form-control mb-2" placeholder="Search..." v-model="search" />

    <!-- PROGRESS BAR -->
    <div v-if="isImporting" class="mb-3">
      <div class="progress">
        <div class="progress-bar progress-bar-striped progress-bar-animated" role="progressbar"
          :style="{ width: progress + '%' }">
          {{ progress }}%
        </div>
      </div>
      <small class="text-muted">
        Imported {{ processedRows }} of {{ totalRows }} rows
      </small>
    </div>
    <!-- STATS -->

    <!-- TABLE -->
    <div class="table-responsive" style="max-height: 420px; overflow-y: auto;">
      <div class="mb-3">
        <label class="form-label">Disbursement Date:</label>
        <input type="date" class="form-control" v-model="batchDisbursementDate" />
      </div>

      <table class="table table-sm table-bordered">
        <thead class="table-light">
          <tr>
            <th>#</th>
            <th>Full Name</th>
            <th>ID</th>
            <th>Member Number</th>
            <th>Amount</th>
            <th>Remark</th>
            <th>Action</th>
          </tr>
        </thead>

        <tbody>
          <tr v-for="(r, i) in filteredRows" :key="i" :class="rowClass(r)">
            <td>{{ i + 1 }}</td>

            <td>
              <input class="form-control form-control-sm" v-model="r.fullName" @input="validateRow(r)" />
            </td>

            <td>
              <input class="form-control form-control-sm" v-model="r.idNumber" @input="validateRow(r)" />
            </td>

            <td>
              <input class="form-control form-control-sm" v-model="r.memberNumber" @input="validateRow(r)" />
            </td>

            <td>
              <input class="form-control form-control-sm" v-model="r.amount" @input="validateRow(r)" />
            </td>

            <td>
              <span v-if="r._status === 'SUCCESS'" class="text-success">
                ✔ {{ r._remark }}
              </span>
              <span v-else-if="r._status === 'FAILED'" class="text-danger">
                ✖ {{ r._remark }}
              </span>
              <span v-else class="text-muted">
                Pending
              </span>
            </td>

            <td>
              <button class="btn btn-sm btn-danger" @click="deleteRow(i)">
                ✖
              </button>
            </td>
          </tr>
        </tbody>
      </table>
    </div>

    <!-- UNDO DELETE -->
    <button v-if="deletedRows.length" class="btn btn-warning mt-2" @click="undoDelete">
      Undo Delete
    </button>

    <!-- SUBMIT -->
    <button class="btn btn-success mt-3" @click="submitRows">
      Submit Valid Rows
    </button>

  </div>

</template>

<script>
import * as XLSX from 'xlsx'
import payrollApi from '@/api/payrollApi'
import ASLDeductions from '@/components/tables/ASLDeductions.vue';

export default {
  components: { ASLDeductions },
  data() {
    return {
      rows: [],
      deletedRows: [],
      search: '',

      // progress
      isImporting: false,
      progress: 0,
      processedRows: 0,
      totalRows: 0
    }
  },

  computed: {
    filteredRows() {
      if (!this.search) return this.rows
      return this.rows.filter(r =>
        Object.values(r).some(v =>
          String(v).toLowerCase().includes(this.search.toLowerCase())
        )
      )
    },

    duplicateCount() {
      return this.rows.filter(r => r._duplicate).length
    },

    invalidCount() {
      return this.rows.filter(r => Object.keys(r._errors).length).length
    },

    validCount() {
      return this.rows.length - this.invalidCount - this.duplicateCount
    }
  },

  methods: {
    normalizeDate(value) {
      if (!value) return ''

      // Excel serial date
      if (typeof value === 'number') {
        const utcDays = Math.floor(value - 25569)
        const date = new Date(utcDays * 86400 * 1000)
        return date.toISOString().slice(0, 10)
      }

      // Already ISO
      if (/^\d{4}-\d{2}-\d{2}$/.test(value)) {
        return value
      }

      // dd-mm-yyyy or dd/mm/yyyy
      const match = value.match(/^(\d{2})[/-](\d{2})[/-](\d{4})$/)
      if (match) {
        const [, dd, mm, yyyy] = match
        return `${yyyy}-${mm}-${dd}`
      }

      // Fallback
      const d = new Date(value)
      if (!isNaN(d)) {
        return d.toISOString().slice(0, 10)
      }

      return ''
    },

    handleFile(e) {
      const reader = new FileReader()

      reader.onload = ev => {
        // Read Excel as ArrayBuffer (robust for numeric and string cells)
        const wb = XLSX.read(ev.target.result, { type: 'array' })
        const sheet = wb.Sheets[wb.SheetNames[0]]

        // Convert sheet to JSON with default empty string
        const data = XLSX.utils.sheet_to_json(sheet, { defval: '' })

        // Map rows to your table structure
        this.rows = data.map(r => ({
          fullName: r["Full Name"] || '',
          idNumber: r["ID Number"] || '',
          memberNumber: r["Member Number"] || '',

          // Robust parsing: remove everything except digits, dot, minus
          amount: Number(String(r["Actual Deduction"] || '').replace(/[^\d.-]/g, '')) || 0,

          _errors: {},
          _duplicate: false,
          _status: 'PENDING', // PENDING | SUCCESS | FAILED
          _remark: ''
        }))

        // Validate each row and check duplicates
        this.rows.forEach(this.validateRow)
        this.detectDuplicates()
      }

      // Use ArrayBuffer for robust reading
      reader.readAsArrayBuffer(e.target.files[0])
    },

    validateRow(r) {
      r._errors = {}
      if (!r.fullName) r._errors.fullName = true
      if (!r.idNumber) r._errors.idNumber = true
      if (!r.memberNumber) r._errors.memberNumber = true
      if (!r.amount || r.amount <= 0) r._errors.amount = true

      this.detectDuplicates()
    },

    detectDuplicates() {
      const seenId = {}
      const seenMemberNumber = {}

      this.rows.forEach(r => {
        r._duplicate = false

        if (
          (r.idNumber && seenId[r.idNumber]) ||
          (r.memberNumber && seenMemberNumber[r.memberNumber])
        ) {
          r._duplicate = true
        }

        seenId[r.idNumber] = true
        seenMemberNumber[r.memberNumber] = true
      })
    },

    deleteRow(i) {
      this.deletedRows.push(this.rows[i])
      this.rows.splice(i, 1)
    },

    undoDelete() {
      this.rows.push(...this.deletedRows)
      this.deletedRows = []
    },

    rowClass(r) {
      if (r._status === 'SUCCESS') return 'table-success'
      if (r._status === 'FAILED') return 'table-danger'
      if (r._duplicate) return 'table-warning'
      if (Object.keys(r._errors).length) return 'table-danger'
      return ''
    },
    async downloadSample() {
      const res = await payrollApi.downloadPayrollExcel(null, {
        onDownloadProgress: (event) => {
          if (event.lengthComputable) {
            this.progress = Math.round(
              (event.loaded * 100) / event.total
            );
          }
        }
      });

      const blob = await res.blob()
      const url = window.URL.createObjectURL(blob)

      const a = document.createElement('a')
      a.href = url
      a.download = 'Payroll_Template.xlsx'
      a.click()

      window.URL.revokeObjectURL(url)
    },

    async submitRows() {
      const validRows = this.rows
        .map((r, index) => ({ r, index }))
        .filter(x =>
          !x.r._duplicate &&
          Object.keys(x.r._errors).length === 0
        )

      if (!validRows.length) {
        alert('No valid rows to submit')
        return
      }

      const CHUNK_SIZE = 10
      const chunks = []

      for (let i = 0; i < validRows.length; i += CHUNK_SIZE) {
        chunks.push(validRows.slice(i, i + CHUNK_SIZE))
      }

      this.isImporting = true
      this.progress = 0
      this.processedRows = 0
      this.totalRows = validRows.length

      for (const chunk of chunks) {
        const payload = chunk.map(x => ({
          index: x.index,
          memberNumber: x.r.memberNumber,
          amount: x.r.amount,
          disbursementDate: this.batchDisbursementDate, // same date for all
        }))

        try {
          const res = await payrollApi.importBatch(payload)

          res.data.forEach(result => {
            const row = this.rows[result.index]
            row._status = result.status
            row._remark = result.message
          })

        } catch (err) {
          // Mark entire chunk as failed
          chunk.forEach(x => {
            x.r._status = 'FAILED'
            x.r._remark = 'System error'
          })
        }

        this.processedRows += chunk.length
        this.progress = Math.round(
          (this.processedRows / this.totalRows) * 100
        )
      }

      this.isImporting = false
    }
  }
}

</script>